import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { z } from 'zod';
import { FaBox, FaCreditCard, FaAngleDown, FaBoxOpen, FaTruck, FaTimes, FaSave, FaPlus, FaTrashAlt } from 'react-icons/fa';
import { FaMapMarkerAlt, FaFileInvoice } from 'react-icons/fa';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { IoIosCloseCircle } from "react-icons/io";
import { zodResolver } from '@hookform/resolvers/zod';
import { useMyContext } from '../../../context/MyContext';
import { FaWarehouse } from "react-icons/fa6";
import '../../css/custom-scrollbar.css';
import { Base_Url,   updateB2b,b2bWarehouseListAll } from '../../../config/config';
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';

const schema = z.object({
    // Basic Shipment Information
    order_id: z.string()
        .min(1, "Order ID is required")
        .regex(/^[A-Za-z0-9-_]+$/, "Order ID must contain only alphanumeric characters, hyphens, and underscores"),

    // lrn: z.string()
    //     .min(1, "LRN is required")
    //     .regex(/^[A-Za-z0-9-_]+$/, "LRN must contain only alphanumeric characters, hyphens, and underscores"),

    lrn: z.string().optional(),

    // NEW: warehouse selected from dropdown
    warehouse_id: z.union([z.string(), z.number()]).optional(),

    total_boxes: z.number()
        .min(1, "Total boxes must be at least 1")
        .int("Total boxes must be a whole number"),

    total_weight: z.number()
        .min(0.1, "Total weight must be greater than 0")
        .max(1000000, "Total weight cannot exceed 1,000,000 kg"),

    // CHANGED: empty number inputs come through react-hook-form as NaN (not undefined),
    // and z.number().optional() only skips undefined — NaN was failing validation even
    // though this field is meant to be optional. Preprocess NaN/'' -> undefined first.
    rov_insurance: z.preprocess(
        (val) => (val === '' || val === null || (typeof val === 'number' && Number.isNaN(val)) ? undefined : val),
        z.number().min(0, "ROV Insurance cannot be negative").optional()
    ),

    // CHANGED: length/width/height are now driven by the per box-set UI below
    // instead of a single manual input, so they're optional at the schema level.
    // CHANGED: these are no longer rendered as inputs (replaced by the per box-set
    // dimensions UI) but defaultValues still seeds them as '' — preprocess so an
    // empty string / NaN becomes undefined instead of failing "expected number".
    total_length: z.preprocess(
        (val) => (val === '' || val === null || (typeof val === 'number' && Number.isNaN(val)) ? undefined : val),
        z.number().optional()
    ),
    total_width: z.preprocess(
        (val) => (val === '' || val === null || (typeof val === 'number' && Number.isNaN(val)) ? undefined : val),
        z.number().optional()
    ),
    total_height: z.preprocess(
        (val) => (val === '' || val === null || (typeof val === 'number' && Number.isNaN(val)) ? undefined : val),
        z.number().optional()
    ),

    freight_mode: z.string()
        .min(1, "Freight mode is required")
        .regex(/^[A-Za-z\s]+$/, "Freight mode must only contain letters and spaces"),

    shipment_type: z.string().optional(),
    // .min(1, "Shipment type is required")
    // .regex(/^[A-Za-z\s]+$/, "Shipment type must only contain letters and spaces"),


    shipment_order_id: z.string().min(1, "shipment_order_id is required"),
    shipment_weight: z
        .number()
        .positive("Weight must be greater than 0"),
    shipment_description: z.string().min(1, "shipment_description is required"),
    shipment_box_count :  z
        .number()
        .positive("Box count must be greater than 0"),
    // shipment_weight: z
    //     .number()
    //     .positive("Weight must be greater than 0"),

    insurence: z.string().optional(), // Note: keeping the typo as in original form

    // Pickup Location
    pickup_consignee_name: z.string()
        .min(1, "Pickup consignee name is required")
        .min(2, "Name must be at least 2 characters")
        .max(100, "Name cannot exceed 100 characters"),
    // .regex(/^[a-zA-Z\s.'-]+$/, "Name must only contain letters, spaces, periods, apostrophes, and hyphens"),

    pickup_phone: z.string()
        .min(10, "Phone number must be at least 10 digits")
        .max(10, "Phone number cannot exceed 10 digits")
        .regex(/^[\+]?[\d\s\-\(\)]+$/, "Phone number format is invalid"),

    pickup_address: z.string()
        .min(10, "Pickup address must be at least 10 characters")
        .max(200, "Address cannot exceed 200 characters"),

    pickup_city: z.string()
        .min(1, "Pickup city is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),

    pickup_state: z.string()
        .min(1, "Pickup state is required")
        .min(2, "State must be at least 2 characters")
        .max(50, "State cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),

    pickup_zip: z.string()
        .min(1, "Pickup ZIP code is required")
        .regex(/^[A-Za-z0-9\s-]+$/, "ZIP code format is invalid")
        .min(3, "ZIP code must be at least 3 characters")
        .max(10, "ZIP code cannot exceed 10 characters"),

    // Dropoff Location
    dropoff_consignee_name: z.string()
        .min(1, "Dropoff consignee name is required")
        .min(2, "Name must be at least 2 characters")
        .max(100, "Name cannot exceed 100 characters"),
    // .regex(/^[a-zA-Z\s.'-]+$/, "Name must only contain letters, spaces, periods, apostrophes, and hyphens"),

    dropoff_phone: z.string()
        .min(10, "Phone number must be at least 10 digits")
        .max(15, "Phone number cannot exceed 15 digits")
        .regex(/^[\+]?[\d\s\-\(\)]+$/, "Phone number format is invalid"),

    dropoff_address: z.string()
        .min(10, "Dropoff address must be at least 10 characters")
        .max(200, "Address cannot exceed 200 characters"),

    dropoff_email: z.string()
        .email("Invalid email address")
        .optional()
        .or(z.literal("")),

    dropoff_city: z.string()
        .min(1, "Dropoff city is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),

    dropoff_state: z.string()
        .min(1, "Dropoff state is required")
        .min(2, "State must be at least 2 characters")
        .max(50, "State cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),

    dropoff_zip: z.string()
        .min(1, "Dropoff ZIP code is required")
        .regex(/^[A-Za-z0-9\s-]+$/, "ZIP code format is invalid")
        .min(3, "ZIP code must be at least 3 characters")
        .max(10, "ZIP code cannot exceed 10 characters"),

    // Billing Address
    billing_name: z.string()
        .min(1, "Billing name is required")
        .min(2, "Name must be at least 2 characters")
        .max(100, "Name cannot exceed 100 characters")
        .regex(/^[a-zA-Z\s.'-]+$/, "Name must only contain letters, spaces, periods, apostrophes, and hyphens"),

    billing_company: z.string()
        .min(1, "Company name is required")
        .min(2, "Company name must be at least 2 characters")
        .max(100, "Company name cannot exceed 100 characters"),

    billing_consignor: z.string()
        .min(1, "Consignor is required")
        .min(2, "Consignor must be at least 2 characters")
        .max(100, "Consignor cannot exceed 100 characters")
        .regex(/^[a-zA-Z\s.'-]+$/, "Consignor must only contain letters, spaces, periods, apostrophes, and hyphens"),

    billing_phone: z.string()
        .min(10, "Phone number must be at least 10 digits")
        .max(15, "Phone number cannot exceed 15 digits")
        .regex(/^[\+]?[\d\s\-\(\)]/, "Phone number format is invalid"),

    billing_address: z.string()
        .min(10, "Billing address must be at least 10 characters")
        .max(200, "Address cannot exceed 200 characters"),

    billing_city: z.string()
        .min(1, "Billing city is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),

    billing_state: z.string()
        .min(1, "Billing state is required")
        .min(2, "State must be at least 2 characters")
        .max(50, "State cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),

    billing_pin: z.string()
        .min(1, "PIN code is required")
        .regex(/^[A-Za-z0-9\s-]+$/, "PIN code format is invalid")
        .min(3, "PIN code must be at least 3 characters")
        .max(10, "PIN code cannot exceed 10 characters"),

    billing_pan_number: z.string()
        .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "PAN number must be in format: ABCDE1234F")
        .optional()
        .or(z.literal("")),

    billing_gst_number: z.string()
        .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, "GST number format is invalid")
        .optional()
        .or(z.literal("")),

    // Payment Details
    payment_id: z.string().optional(),
    // .min(1, "Payment ID is required")
    // .regex(/^[A-Za-z0-9-_]+$/, "Payment ID must contain only alphanumeric characters, hyphens, and underscores"),

    // payment_mode: z.enum([
    //     "COD",
    //     "Prepaid",
    // ], {
    //     errorMap: () => ({ message: "Please select a valid payment mode" })
    // }),
    invoice_num: z.string().optional(),
    payment_mode: z.string().optional(),
    inv_amt: z.string().optional(),

});

// Reusable gradient icon badge for section headers
const SectionBadge = ({ icon, gradient }) => (
    <div className={`flex items-center justify-center w-7 h-7 rounded-lg shadow-md bg-gradient-to-br ${gradient} text-white flex-shrink-0`}>
        {icon}
    </div>
);

const B2BViewAllShipment = ({ isopen, onClose, shipment, fetchOrderData, wareHouses = [] }) => {
    const { isDarkMode } = useMyContext();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    // const [warehouseList, setwareHouseList] = useState([])

    // NEW: per box-set dimensions state (quantity + L/W/H per set)
    const [boxSets, setBoxSets] = useState([{ id: 1, box_count: '', length: '', width: '', height: '' }]);
    const boxSetIdRef = useRef(1);

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            order_id: '',
            lrn: '',
            warehouse_id: '',
            payment_mode: '',
            payment_id: '',
            cod_amount: '',
            total_boxes: '',
            total_weight: '',
            total_length: '',
            total_width: '',
            total_height: '',
            rov_insurance: '',
            freight_mode: '',
            shipment_type: '',
            insurence: '',
            // Pickup location fields
            pickup_consignee_name: '',
            pickup_address: '',
            pickup_city: '',
            pickup_state: '',
            pickup_zip: '',
            pickup_phone: '',
            // Dropoff location fields
            dropoff_consignee_name: '',
            dropoff_address: '',
            dropoff_city: '',
            dropoff_state: '',
            dropoff_zip: '',
            dropoff_phone: '',
            dropoff_email: '',
            // Billing address fields
            billing_name: '',
            billing_company: '',
            billing_consignor: '',
            billing_address: '',
            billing_city: '',
            billing_state: '',
            billing_pin: '',
            billing_phone: '',
            billing_pan_number: '',
            billing_gst_number: '',
            // Invoice fields
            invoice_ewaybill: '',
            invoice_num: '',
            invoice_amt: '',
            invoice_qr_code: '',
            // Shipment details
            shipment_order_id: '',
            shipment_box_count: '',
            shipment_description: '',
            shipment_weight: '',
            shipment_master: '',

        }
    });
    const paymentMode = watch('payment_mode');
    const selectedWarehouseId = watch('warehouse_id');
    const totalBoxesWatch = watch('total_boxes'); // NEW: drives box-set auto-sync

    // Check if a field is editable
    const isEditable = (fieldName) => {
        const editableFields = [
            'lrn', 'warehouse_id', 'payment_mode', 'payment_id', 'cod_amount', 'total_boxes',
            'total_weight', 'total_length', 'total_width', 'total_height',
            'rov_insurance', 'freight_mode', 'shipment_type', 'insurence',
            'pickup_consignee_name', 'pickup_address', 'pickup_city', 'pickup_state',
            'pickup_zip', 'pickup_phone', 'dropoff_consignee_name', 'dropoff_address',
            'dropoff_city', 'dropoff_state', 'dropoff_zip', 'dropoff_phone',
            'dropoff_email', 'billing_name', 'billing_company', 'billing_consignor',
            'billing_address', 'billing_city', 'billing_state', 'billing_pin',
            'billing_phone', 'billing_pan_number', 'billing_gst_number',
            'invoice_ewaybill', 'invoice_num', 'invoice_amt', 'invoice_qr_code',
            'shipment_order_id', 'shipment_box_count', 'shipment_description',
            'shipment_weight', 'shipment_master'
        ];
        return editableFields.includes(fieldName) && isEditing; // Only editable when in edit mode
    };

    useEffect(() => {
        if (shipment) {
            try {
                // Parse JSON string fields
                const pickup = shipment.pickup_location ? JSON.parse(shipment.pickup_location) : {};
                const dropoff = shipment.dropoff_location ? JSON.parse(shipment.dropoff_location) : {};
                const billing = shipment.billing_address ? JSON.parse(shipment.billing_address) : {};
                const invoices = shipment.invoices ? JSON.parse(shipment.invoices) : [];
                const shipmentDetails = shipment.shipment_details ? JSON.parse(shipment.shipment_details) : [];

                // Set basic shipment fields
                setValue('order_id', shipment.order_id || '');
                setValue('lrn', shipment.lrn || '');
                setValue('payment_mode', shipment.payment_mode || '');
                setValue('payment_id', shipment.payment_id || '');
                setValue('cod_amount', shipment.cod_amount || '');
                setValue('total_boxes', shipment.total_boxes || '');
                setValue('total_weight', shipment.total_weight || '');
                setValue('total_length', shipment.total_length || '');
                setValue('total_width', shipment.total_width || '');
                setValue('total_height', shipment.total_height || '');
                setValue('rov_insurance', shipment.rov_insurance || '');
                setValue('freight_mode', shipment.freight_mode || '');
                setValue('shipment_type', shipment.shipment_type || '');
                setValue('insurence', shipment.insurence || '');

                // Set pickup location fields
                setValue('pickup_consignee_name', pickup.consignee_name || '');
                setValue('pickup_address', pickup.address || '');
                setValue('pickup_city', pickup.city || '');
                setValue('pickup_state', pickup.state || '');
                setValue('pickup_zip', pickup.zip || '');
                setValue('pickup_phone', pickup.phone || '');

                // Set dropoff location fields
                setValue('dropoff_consignee_name', dropoff.consignee_name || '');
                setValue('dropoff_address', dropoff.address || '');
                setValue('dropoff_city', dropoff.city || '');
                setValue('dropoff_state', dropoff.state || '');
                setValue('dropoff_zip', dropoff.zip || '');
                setValue('dropoff_phone', dropoff.phone || '');
                setValue('dropoff_email', dropoff.email || '');

                // Set billing address fields
                setValue('billing_name', billing.name || '');
                setValue('billing_company', billing.company || '');
                setValue('billing_consignor', billing.consignor || '');
                setValue('billing_address', billing.address || '');
                setValue('billing_city', billing.city || '');
                setValue('billing_state', billing.state || '');
                setValue('billing_pin', billing.pin || '');
                setValue('billing_phone', billing.phone || '');
                setValue('billing_pan_number', billing.pan_number || '');
                setValue('billing_gst_number', billing.gst_number || '');

                // Set invoice fields (first invoice if exists)
                if (invoices.length > 0) {
                    const firstInvoice = invoices[0];
                    setValue('invoice_ewaybill', firstInvoice.ewaybill || '');
                    setValue('invoice_num', firstInvoice.inv_num || '');
                    setValue('invoice_amt', firstInvoice.inv_amt || '');
                    setValue('invoice_qr_code', firstInvoice.inv_qr_code || '');
                }

                // Set shipment details (first shipment if exists)
                if (shipmentDetails.length > 0) {
                    const firstShipment = shipmentDetails[0];
                    setValue('shipment_order_id', firstShipment.order_id || '');
                    setValue('shipment_box_count', firstShipment.box_count || '');
                    setValue('shipment_description', firstShipment.description || '');
                    setValue('shipment_weight', firstShipment.weight || '');
                    setValue('shipment_master', firstShipment.master || '');
                }

                // NEW: Load per box-set dimensions from shipment.dimensions
                // (falls back to a single set built from total_length/width/height).
                try {
                    const dims = shipment.dimensions ? JSON.parse(shipment.dimensions) : [];
                    if (Array.isArray(dims) && dims.length > 0) {
                        boxSetIdRef.current = dims.length;
                        setBoxSets(
                            dims.map((d, idx) => ({
                                id: idx + 1,
                                box_count: String(d.box_count ?? ''),
                                length: String(d.length ?? ''),
                                width: String(d.width ?? ''),
                                height: String(d.height ?? ''),
                            }))
                        );
                    } else {
                        boxSetIdRef.current = 1;
                        setBoxSets([{
                            id: 1,
                            box_count: String(shipment.total_boxes || ''),
                            length: String(shipment.total_length || ''),
                            width: String(shipment.total_width || ''),
                            height: String(shipment.total_height || ''),
                        }]);
                    }
                } catch (e) {
                    boxSetIdRef.current = 1;
                    setBoxSets([{ id: 1, box_count: String(shipment.total_boxes || ''), length: '', width: '', height: '' }]);
                }

            } catch (error) {
                console.error('Error parsing shipment data:', error);
                setError('Error parsing shipment data');
            }
        }
    }, [shipment, setValue]);

    // NEW: Pre-select warehouse in the dropdown by matching pickup consignee name
    // against the warehouse list (falls back to whatever the API already stored,
    // if the shipment carries a warehouse_id field directly).
    useEffect(() => {
        if (!shipment) return;
        if (shipment.warehouse_id) {
            setValue('warehouse_id', shipment.warehouse_id);
            return;
        }
        if (!wareHouses || wareHouses.length === 0) return;
        try {
            const pickup = shipment.pickup_location ? JSON.parse(shipment.pickup_location) : {};
            const consigneeName = pickup?.consignee_name?.trim()?.toLowerCase();
            if (!consigneeName) return;
            const matched = wareHouses.find(
                (wh) => wh?.name?.trim()?.toLowerCase() === consigneeName
            );
            if (matched) {
                setValue('warehouse_id', matched.warehouse_id ?? matched.id ?? '');
            }
        } catch (e) {
            console.error('Error matching warehouse', e);
        }
    }, [shipment, wareHouses, setValue]);

    // NEW: Keep the single box-set's quantity auto-synced with Total Boxes
    // (the "all boxes same dimension" case). As soon as a 2nd set is added,
    // quantities become independently editable.
    useEffect(() => {
        setBoxSets((prev) => {
            if (prev.length === 1) {
                return [{ ...prev[0], box_count: totalBoxesWatch === '' || totalBoxesWatch == null ? '' : String(totalBoxesWatch) }];
            }
            return prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalBoxesWatch]);

    // NEW: box-set helpers
    const boxAssignedCount = boxSets.reduce((sum, s) => sum + (parseInt(s.box_count, 10) || 0), 0);
    const boxTotalNum = parseInt(totalBoxesWatch, 10) || 0;
    const boxRemaining = boxTotalNum - boxAssignedCount;

    const updateBoxSet = (id, field, value) => {
        setBoxSets((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
    };

    const addBoxSet = () => {
        if (!boxTotalNum) {
            toast.error('Enter Total Boxes ');
            return;
        }
        if (boxRemaining <= 0) {
            toast.error('All Boxes has already assigned.');
            return;
        }
        boxSetIdRef.current += 1;
        setBoxSets((prev) => [...prev, { id: boxSetIdRef.current, box_count: String(boxRemaining), length: '', width: '', height: '' }]);
    };

    const removeBoxSet = (id) => {
        setBoxSets((prev) => (prev.length > 1 ? prev.filter((s) => s.id !== id) : prev));
    };

    // CHANGED: instead of a generic "Please fix the errors in the form." toast,
    // surface the actual field name + message so it's clear what to fix.
    const onErrors = (errors) => {
        const firstKey = Object.keys(errors)[0];
        const firstError = firstKey ? errors[firstKey] : null;
        if (firstError?.message) {
            toast.error(`${firstKey}: ${firstError.message}`);
        } else {
            toast.error("Please fix the errors in the form.");
        }
        console.error("Form validation errors:", errors);
    };

    const onSubmit = async (data) => {

        // NEW: validate box-set dimensions before building the payload
        for (const s of boxSets) {
            const qty = parseInt(s.box_count, 10) || 0;
            if (qty < 1 || !s.length || !s.width || !s.height) {
                toast.error('fill out all box set dimensions and ensure box count is at least 1.');
                return;
            }
        }
        if (boxAssignedCount !== boxTotalNum) {
            toast.error(
                boxAssignedCount < boxTotalNum
                    ? `${boxRemaining} boxes dimensions are missing.`
                    : ` ${Math.abs(boxRemaining)} `
            );
            return;
        }

        const url = Base_Url + updateB2b + "/" + data.order_id;
        try {
            setIsLoading(true);
            setError(null);

            // Construct pickup location object
            const pickupLocation = {
                consignee_name: data.pickup_consignee_name,
                address: data.pickup_address,
                city: data.pickup_city,
                state: data.pickup_state,
                zip: data.pickup_zip,
                phone: data.pickup_phone,
            };

            // Construct dropoff location object
            const dropoffLocation = {
                consignee_name: data.dropoff_consignee_name,
                address: data.dropoff_address,
                city: data.dropoff_state,
                state: data.dropoff_state,
                zip: data.dropoff_zip,
                phone: data.dropoff_phone,
                email: data.dropoff_email
            };

            // Construct billing address object
            const billingAddress = {
                name: data.billing_name,
                company: data.billing_company,
                consignor: data.billing_consignor,
                address: data.billing_address,
                city: data.billing_city,
                state: data.billing_state,
                pin: data.billing_pin,
                phone: data.billing_phone,
                pan_number: data.billing_pan_number,
                gst_number: data.billing_gst_number
            };

            // Construct invoice array
            const invoices = [{
                ewaybill: data.invoice_ewaybill,
                inv_num: data.invoice_num,
                inv_amt: parseFloat(data.invoice_amt) || 0,
                inv_qr_code: data.invoice_qr_code || data.shipment_order_id
            }];
            const docData = [
                {
                    doc_type: "INVOICE_COPY",
                    doc_meta: {
                        invoice_num: [data.invoice_num]
                    }
                }
            ];


            // Construct shipment details array

            const shipmentDetails = [{
                order_id: data.shipment_order_id,
                box_count: data.shipment_box_count || 0,
                description: data.shipment_description,
                weight: data.shipment_weight || 0,
                waybills: [],
                master: data.shipment_master
            }];

            // CHANGED: dimensions now come from the box-set UI (supports multiple
            // box-sets with different L/W/H) instead of a single hardcoded set.
            const dimensions = boxSets.map((s) => ({
                box_count: parseInt(s.box_count, 10) || 0,
                length: parseFloat(s.length) || 0,
                width: parseFloat(s.width) || 0,
                height: parseFloat(s.height) || 0,
            }));

            // Use the warehouse actually selected in the form (edit mode),
            // this now reflects whatever the user picked in the dropdown.
            if (!data.warehouse_id) {
                toast.error("Please select a warehouse before saving.");
                setIsLoading(false);
                return;
            }

            const payload = {
                order_id: data.order_id,
                warehouse_id: data.warehouse_id,
                retailer_id: shipment.user_id,
                lrn: data.lrn,
                payment_mode: data.payment_mode,
                cod_amount: data.payment_mode === "prepaid" ? 0 : data.cod_amount,
                payment_id: data.payment_id,    
                total_boxes: data.total_boxes,
                total_weight: data.total_weight,
                weight: data.total_weight,
                // CHANGED: derived from the first box-set for backward compatibility
                total_length: dimensions[0]?.length ?? 0,
                total_width: dimensions[0]?.width ?? 0,
                total_height: dimensions[0]?.height ?? 0,
                rov_insurance: data.rov_insurance,
                freight_mode: data.freight_mode,
                shipment_type: data.shipment_type,
                insurence: data.insurence,
                dimensions: JSON.stringify(dimensions),
                pickup_location: JSON.stringify(pickupLocation),
                dropoff_location: JSON.stringify(dropoffLocation),
                billing_address: JSON.stringify(billingAddress),
                invoices: JSON.stringify(invoices),
                doc_file: shipment.doc_file_path,
                doc_data: JSON.stringify(docData),
                shipment_details: JSON.stringify(shipmentDetails)

            };

            // Add cod_amount if payment mode is not prepaid
            if (data.payment_mode !== 'prepaid') {
                payload.cod_amount = data.cod_amount;
            }

            // Call API to update shipment
            const response = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'token': token,
                    'cus_id': cus_id,
                },
            });

            if (response.data.status === "SUCCESS") {
                toast.success(response.data.message);
                setIsEditing(false);
                onClose();
                fetchOrderData && fetchOrderData();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update shipment');
            toast.error(err.response?.data?.message || 'Failed to update shipment');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    // Render an input field - either editable or read-only
    const renderField = (label, fieldName, type = 'text', isNumeric = false, placeholder = '') => {
        const editable = isEditable(fieldName);
        let value = '';

        // Handle different data sources based on field name
        if (fieldName.startsWith('pickup_') && shipment?.pickup_location) {
            try {
                const pickup = JSON.parse(shipment.pickup_location);
                const key = fieldName.replace('pickup_', '');
                value = pickup[key] || '';
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('dropoff_') && shipment?.dropoff_location) {
            try {
                const dropoff = JSON.parse(shipment.dropoff_location);
                const key = fieldName.replace('dropoff_', '');
                value = dropoff[key] || '';
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('billing_') && shipment?.billing_address) {
            try {
                const billing = JSON.parse(shipment.billing_address);
                const key = fieldName.replace('billing_', '');
                value = billing[key] || '';
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('invoice_') && shipment?.invoices) {
            try {
                const invoices = JSON.parse(shipment.invoices);
                if (invoices.length > 0) {
                    const key = fieldName.replace('invoice_', '');
                    // Map some fields
                    if (key === 'amt') {
                        value = invoices[0].inv_amt || '';
                    } else if (key === 'num') {
                        value = invoices[0].inv_num || '';
                    } else if (key === 'qr_code') {
                        value = invoices[0].inv_qr_code || '';
                    } else {
                        value = invoices[0][key] || '';
                    }
                }
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('shipment_') && shipment?.shipment_details) {
            try {
                const shipmentDetails = JSON.parse(shipment.shipment_details);
                if (shipmentDetails.length > 0) {
                    const key = fieldName.replace('shipment_', '');
                    if (key === 'order_id') {
                        value = shipmentDetails[0].order_id || '';
                    } else if (key === 'box_count') {
                        value = shipmentDetails[0].box_count || '';
                    } else if (key === 'description') {
                        value = shipmentDetails[0].description || '';
                    } else if (key === 'weight') {
                        value = shipmentDetails[0].weight || '';
                    } else if (key === 'master') {
                        value = shipmentDetails[0].master || '';
                    }
                }
            } catch (e) {
                value = '';
            }
        } else {
            // Direct field from shipment
            value = shipment?.[fieldName] || '';
            if (fieldName === 'cod_amount' && !value && shipment?.cod) {
                value = shipment.cod;
            }
        }




        return (
            <div>
                <label className="block text-[10px] mb-1 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</label>
                {editable ? (
                    <>
                        <input
                            type={type}
                            step={isNumeric ? "0.1" : undefined}
                            placeholder={placeholder}
                            className={`px-2.5 py-2 text-sm rounded-lg border w-full outline-none transition-all duration-200 shadow-sm focus:shadow-md focus:ring-2 ${isDarkMode
                                ? "bg-gray-800/70 border-gray-700 text-white placeholder-gray-500 focus:border-indigo-400 focus:ring-indigo-500/20"
                                : "bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-indigo-400 focus:ring-indigo-500/15"
                                }`}
                            {...register(fieldName, isNumeric ? { valueAsNumber: true } : {})}
                        />
                        {errors[fieldName] && <p className="text-red-500 text-[10px] mt-0.5 font-medium">{errors[fieldName].message}</p>}
                    </>
                ) : (
                    <div className={`px-2.5 py-2 text-sm rounded-lg border w-full truncate ${isDarkMode
                        ? "bg-gray-800/40 border-gray-700/60 text-gray-100"
                        : "bg-gray-50 border-gray-100 text-gray-700"
                        }`}>
                        <span className="font-medium">{value || <span className="italic text-gray-400 font-normal">Not specified</span>}</span>
                    </div>
                )}
            </div>
        );
    };

    // NEW: Warehouse select field — dropdown when editing, read-only chip otherwise
    const renderWarehouseField = () => {
        const editable = isEditable('warehouse_id');
        const selectedWh = wareHouses.find(
            (wh) => String(wh.warehouse_id ?? wh.id) === String(selectedWarehouseId)
        );

        return (
            <div>
                <label className="block text-[10px] mb-1 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Warehouse
                </label>
                {editable ? (
                    <>
                        <div className="relative">
                            <select
                                {...register('warehouse_id')}
                                className={`px-2.5 py-2 text-sm rounded-lg border w-full outline-none appearance-none transition-all shadow-sm focus:shadow-md focus:ring-2 ${isDarkMode
                                    ? "bg-gray-800/70 text-white border-gray-700 focus:border-indigo-400 focus:ring-indigo-500/20"
                                    : "bg-white border-gray-200 focus:border-indigo-400 focus:ring-indigo-500/15"
                                    }`}
                            >
                                <option value="">Select Warehouse</option>
                                {wareHouses.map((wh) => {
                                    const whId = wh.warehouse_id ?? wh.id;
                                    return (
                                        <option key={whId} value={whId}>
                                            {wh.name}
                                        </option>
                                    );
                                })}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center px-2.5 pointer-events-none">
                                <FaAngleDown className="text-gray-400" size={11} />
                            </div>
                        </div>
                        {wareHouses.length === 0 && (
                            <p className="text-amber-500 text-[10px] mt-0.5 font-medium">No warehouses available to select</p>
                        )}
                        {errors.warehouse_id && (
                            <p className="text-red-500 text-[10px] mt-0.5 font-medium">{errors.warehouse_id.message}</p>
                        )}
                    </>
                ) : (
                    <div className={`px-2.5 py-2 text-sm rounded-lg border w-full flex items-center gap-1.5 truncate ${isDarkMode
                        ? "bg-gray-800/40 border-gray-700/60 text-gray-100"
                        : "bg-gray-50 border-gray-100 text-gray-700"
                        }`}>
                        <FaWarehouse className="text-gray-400 flex-shrink-0" size={11} />
                        <span className="font-medium truncate">
                            {selectedWh?.name || <span className="italic text-gray-400 font-normal">Not specified</span>}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    // NEW: Box Dimensions UI — compact, per box-set (quantity/length/width/height) card,
    // auto-syncs single-set quantity with Total Boxes, editable only in edit mode.
    // CHANGED: list is now capped and scrolls (custom-scrollbar) beyond that, so
    // 50-box shipments with many sets don't blow up the dialog height.
    const renderBoxDimensionsSection = () => {
        const inputCls = `w-full text-sm rounded-lg border px-2 py-1.5 outline-none transition-all focus:ring-2 disabled:opacity-60 disabled:cursor-not-allowed ${
            isDarkMode
                ? "bg-gray-800/70 border-gray-700 text-white placeholder-gray-500 focus:border-indigo-400 focus:ring-indigo-500/20"
                : "bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-indigo-400 focus:ring-indigo-500/15"
        }`;
        const labelCls = `block text-[9px] mb-0.5 font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`;
        const cardCls = `rounded-lg border p-2 ${isDarkMode ? "bg-gray-800/40 border-gray-700/60" : "bg-gray-50 border-gray-100"}`;

        if (!isEditing) {
            const items = boxSets.filter((s) => s.length || s.width || s.height || s.box_count);
            const roLabelCls = `block text-[9px] mb-0.5 font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`;
            const roValueCls = `px-2 py-1.5 text-sm rounded-lg border w-full ${isDarkMode ? "bg-gray-800/70 border-gray-700 text-gray-100" : "bg-white border-gray-200 text-gray-800"}`;

            return (
                <div>
                  <div className="border p-2.5 rounded-lg">
                      <label className="block text-[10px] mb-1 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Box Dimensions
                    </label>
                    <div className="max-h-[150px] overflow-y-auto pr-1 custom-scrollbar space-y-1.5">
                        {items.map((s) => (
                            <div key={s.id} className={cardCls}>
                                <div className="grid grid-cols-4 gap-1.5">
                                    <div>
                                        <label className={roLabelCls}>Qty</label>
                                        <div className={roValueCls}>{s.box_count || 0}</div>
                                    </div>
                                    <div>
                                        <label className={roLabelCls}>Length</label>
                                        <div className={roValueCls}>{s.length || 0}</div>
                                    </div>
                                    <div>
                                        <label className={roLabelCls}>Width</label>
                                        <div className={roValueCls}>{s.width || 0}</div>
                                    </div>
                                    <div>
                                        <label className={roLabelCls}>Height</label>
                                        <div className={roValueCls}>{s.height || 0}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {items.length === 0 && (
                            <div className={`px-2.5 py-2 text-sm rounded-lg border w-full ${isDarkMode ? "bg-gray-800/40 border-gray-700/60 text-gray-100" : "bg-gray-50 border-gray-100 text-gray-700"}`}>
                                <span className="italic text-gray-400 font-normal">Not specified</span>
                            </div>
                        )}
                    </div>
                  </div>
                </div>
            );
        }

        return (
            <div>
               <div className="border p-2.5 rounded-lg">
                 <div className="flex items-center justify-between mb-1">
                    <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Box Dimensions
                    </label>
                    {boxTotalNum > 0 && (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            boxRemaining === 0
                                ? isDarkMode ? "bg-emerald-500/10 text-emerald-400" : "bg-emerald-50 text-emerald-700"
                                : isDarkMode ? "bg-amber-500/10 text-amber-400" : "bg-amber-50 text-amber-700"
                        }`}>
                            {boxAssignedCount}/{boxTotalNum} assigned{boxRemaining !== 0 ? ` • ${boxRemaining} left` : ''}
                        </span>
                    )}
                </div>

                {/* CHANGED: scrollable box, rest scrolls */}
                <div className="max-h-[150px] overflow-y-auto pr-1 custom-scrollbar space-y-1.5">
                    {boxSets.map((s) => (
                        <div key={s.id} className={cardCls}>
                            <div className="grid grid-cols-4 gap-1.5 items-end">
                                <div>
                                    <label className={labelCls}>Qty</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={s.box_count}
                                        onChange={(e) => updateBoxSet(s.id, 'box_count', e.target.value)}
                                        className={inputCls}
                                        disabled={boxSets.length === 1}
                                    />
                                </div>
                                <div>
                                    <label className={labelCls}>Length</label>
                                    <input
                                        type="number"
                                        min="0.1"
                                        step="0.1"
                                        value={s.length}
                                        onChange={(e) => updateBoxSet(s.id, 'length', e.target.value)}
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label className={labelCls}>Width</label>
                                    <input
                                        type="number"
                                        min="0.1"
                                        step="0.1"
                                        value={s.width}
                                        onChange={(e) => updateBoxSet(s.id, 'width', e.target.value)}
                                        className={inputCls}
                                    />
                                </div>
                                <div className="flex items-end gap-1">
                                    <div className="flex-1">
                                        <label className={labelCls}>Height</label>
                                        <input
                                            type="number"
                                            min="0.1"
                                            step="0.1"
                                            value={s.height}
                                            onChange={(e) => updateBoxSet(s.id, 'height', e.target.value)}
                                            className={inputCls}
                                        />
                                    </div>
                                    {boxSets.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeBoxSet(s.id)}
                                            className={`w-7 h-[30px] flex items-center justify-center rounded-lg border transition-colors ${
                                                isDarkMode ? "border-gray-700 text-red-400 hover:bg-red-500/10" : "border-gray-200 text-red-500 hover:bg-red-50"
                                            }`}
                                        >
                                            <FaTrashAlt size={10} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
               </div>

                <button
                    type="button"
                    onClick={addBoxSet}
                    className={`mt-1.5 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-dashed text-[11px] font-semibold transition-colors ${
                        isDarkMode ? "border-gray-700 text-gray-300 hover:bg-gray-800" : "border-gray-300 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                    <FaPlus size={9} /> Add Box Set
                </button>
            </div>
        );
    };

    const handleClose = () => {
        // setIsEditing(false); // Reset edit mode when closing
        // reset();
        onClose();
    };

    if (!shipment) return null;

    return (
        <Dialog
            open={isopen}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                className: `rounded-2xl overflow-hidden ${isDarkMode ? "!bg-darkmodeColor" : "!bg-gray-50"}`
            }}
        >
            {/* Sticky gradient header */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 px-4 py-3 shadow-lg">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                            <FaWarehouse className="text-white" size={15} />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-white/70 font-semibold mb-0.5">B2B Shipment</p>
                            <h2 className="text-sm md:text-base font-bold text-white">#{shipment?.order_id || 'Details'}</h2>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm transition-all hover:scale-105"
                    >
                        <IoIosCloseCircle size={20} className="text-white" />
                    </button>
                </div>
                <div className="absolute -bottom-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
            </div>

            <DialogContent className={`custom-scrollbar px-3 md:px-4 pb-4 pt-3 ${isDarkMode ? "dark:bg-darkmodeColor" : "bg-gray-50"}`} style={{ maxHeight: '75vh' }}>

                {!isEditing && (
                    <div className="flex justify-end mb-2.5">
                        <button
                            type="button"
                            onClick={handleEdit}
                            disabled={shipment?.status !== 'Pending'}
                            className={`py-2 px-4 rounded-lg focus:outline-none focus:ring-2 font-semibold text-xs transition-all duration-200 flex items-center gap-1.5 shadow-sm ${shipment?.status === 'Pending'
                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5 focus:ring-blue-500/20'
                                : `${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-white text-gray-400'} cursor-not-allowed border border-gray-200 dark:border-gray-700`
                                }`}
                        >
                            <FaEdit size={12} /> Edit Shipment
                        </button>
                    </div>
                )}

                <form className="space-y-3 font-sans" onSubmit={handleSubmit(onSubmit, onErrors)}>
                    {/* Basic Shipment Information */}
                    <div className={`rounded-xl p-3 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaBox size={12} />} gradient="from-purple-500 to-fuchsia-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Basic Shipment Information
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {renderField('Order ID', 'order_id')}
                            {renderField('LRN', 'lrn')}
                            {renderWarehouseField()}
                            {renderField('Total Boxes', 'total_boxes', 'number', true)}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            {renderField('Total Weight', 'total_weight', 'number', true)}
                            {renderField('ROV Insurance', 'rov_insurance', 'number', true)}
                            {renderField('Freight Mode', 'freight_mode')}
                            {renderField('Shipment Type', 'shipment_type')}
                        </div>
                        <div className="grid grid-cols-1 gap-3 mt-3">
                            {renderField('Insurance', 'insurence')}
                        </div>
                        {/* CHANGED: Total Length/Width/Height replaced by the per box-set Box Dimensions UI */}
                        <div className="grid grid-cols-1 gap-3 mt-3">
                            {renderBoxDimensionsSection()}
                        </div>
                    </div>

                    {/* Pickup Location Section */}
                    <div className={`rounded-xl p-3 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaTruck size={11} />} gradient="from-sky-500 to-blue-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Pickup Location
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {renderField('Consignee Name', 'pickup_consignee_name')}
                            {renderField('Phone', 'pickup_phone')}
                            {renderField('City', 'pickup_city')}
                            {renderField('ZIP Code', 'pickup_zip')}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            <div className="col-span-2 md:col-span-3">
                                {renderField('Address', 'pickup_address')}
                            </div>
                            {renderField('State', 'pickup_state')}
                        </div>
                    </div>

                    {/* Dropoff Location Section */}
                    <div className={`rounded-xl p-3 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaMapMarkerAlt size={11} />} gradient="from-emerald-500 to-teal-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Dropoff Location
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {renderField('Consignee Name', 'dropoff_consignee_name')}
                            {renderField('Phone', 'dropoff_phone')}
                            {renderField('City', 'dropoff_city')}
                            {renderField('ZIP Code', 'dropoff_zip')}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            <div className="col-span-2">
                                {renderField('Address', 'dropoff_address')}
                            </div>
                            {renderField('Email', 'dropoff_email')}
                            {renderField('State', 'dropoff_state')}
                        </div>
                    </div>

                    {/* Billing Address Section */}
                    <div className={`rounded-xl p-3 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaFileInvoice size={11} />} gradient="from-amber-500 to-orange-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Billing Address
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {renderField('Name', 'billing_name')}
                            {renderField('Company', 'billing_company')}
                            {renderField('Consignor', 'billing_consignor')}
                            {renderField('Phone', 'billing_phone')}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            <div className="col-span-2">
                                {renderField('Address', 'billing_address')}
                            </div>
                            {renderField('City', 'billing_city')}
                            {renderField('State', 'billing_state')}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            {renderField('PIN Code', 'billing_pin')}
                            {renderField('PAN Number', 'billing_pan_number')}
                            {renderField('GST Number', 'billing_gst_number')}
                        </div>
                    </div>

                    {/* Payment Details Section */}
                    <div className={`rounded-xl p-3 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaCreditCard size={11} />} gradient="from-indigo-500 to-violet-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Payment Details
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {renderField('Payment ID', 'payment_id')}
                            <div>
                                <label className="block text-[10px] mb-1 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Payment Mode</label>
                                {isEditable('payment_mode') ? (
                                    <>
                                        <div className="relative">
                                            <select
                                                {...register('payment_mode')}
                                                className={`px-2.5 py-2 text-sm rounded-lg border w-full outline-none appearance-none transition-all shadow-sm focus:shadow-md focus:ring-2 ${isDarkMode
                                                    ? "bg-gray-800/70 text-white border-gray-700 focus:border-indigo-400 focus:ring-indigo-500/20"
                                                    : "bg-white border-gray-200 focus:border-indigo-400 focus:ring-indigo-500/15"
                                                    }`}
                                            >
                                                <option value="">Select Mode</option>
                                                <option value="cod">COD</option>
                                                <option value="prepaid">Prepaid</option>
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center px-2.5 pointer-events-none">
                                                <FaAngleDown className="text-gray-400" size={11} />
                                            </div>
                                        </div>
                                        {errors.payment_mode && (
                                            <p className="text-red-500 text-[10px] mt-0.5 font-medium">{errors.payment_mode.message}</p>
                                        )}
                                    </>
                                ) : (
                                    <div className="px-2.5 py-2 text-sm rounded-lg border w-full bg-gradient-to-r from-indigo-500 to-violet-600 border-transparent shadow-sm">
                                        <span className="font-semibold text-xs text-white uppercase tracking-wide">{shipment?.payment_mode || 'Not specified'}</span>
                                    </div>
                                )}
                            </div>
                            {paymentMode === 'cod' && renderField('COD Amount', 'cod_amount', 'number', true)}
                        </div>
                    </div>

                    {/* Invoice Details Section */}
                    <div className={`rounded-xl p-3 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaFileInvoice size={11} />} gradient="from-orange-500 to-red-500" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Invoice Details
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {renderField('E-way Bill', 'invoice_ewaybill')}
                            {renderField('Invoice Number', 'invoice_num')}
                            {renderField('Invoice Amount', 'invoice_amt', 'number', true)}
                            {renderField('QR Code', 'invoice_qr_code')}
                        </div>
                    </div>

                    {/* Shipment Details Section */}
                    <div className={`rounded-xl p-3 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaBoxOpen size={11} />} gradient="from-teal-500 to-cyan-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Shipment Details
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {renderField('Shipment Order ID', 'shipment_order_id')}
                            {renderField('Box Count', 'shipment_box_count', 'number', true)}
                            {renderField('Weight', 'shipment_weight', 'number', true)}
                            {renderField('Master', 'shipment_master')}
                        </div>
                        <div className="grid grid-cols-1 gap-3 mt-3">
                            {renderField('Description', 'shipment_description')}
                        </div>
                    </div>

                    {/* Action Buttons - sticky footer style */}
                    <div className={` md:-mx-4 px-3 md:px-4 py-2 flex flex-wrap justify-end gap-2.5 border-t backdrop-blur-md ${isDarkMode ? "bg-darkmodeColor/90 border-gray-800" : "bg-gray-50/90 border-gray-200"}`}>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="w-full sm:w-auto bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 py-2 px-5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500/10 font-semibold text-xs transition-all flex items-center justify-center gap-1.5"
                        >
                            <FaTimes size={12} /> Cancel
                        </button>
                        {isEditing && (
                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`
                                    w-full sm:w-auto 
                                    bg-gradient-to-r from-purple-600 to-indigo-700 
                                    text-white py-2 px-5 rounded-lg 
                                    hover:shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-0.5
                                    focus:outline-none focus:ring-2 focus:ring-purple-500/20
                                    font-semibold text-xs transition-all duration-200 flex items-center justify-center gap-1.5 shadow-sm
                                    disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:hover:translate-y-0
                                `}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave size={12} /> Save Changes
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default B2BViewAllShipment;